import React, { useState, useEffect } from 'react';
import { useApi, api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import useMobile from '../hooks/useMobile';
import { useTheme } from '../context/ThemeContext';

const INTERVALS = [
  {value:15,label:'Hvert 15. minutt'},{value:30,label:'Hvert 30. minutt'},
  {value:60,label:'Hver time'},{value:360,label:'Hver 6. time'},
  {value:720,label:'Hver 12. time'},{value:1440,label:'En gang daglig'},
];

function normalizeIcalUrl(url) {
  if (!url) return url;
  const u = url.trim();
  if (u.startsWith('webcals://')) return 'https://' + u.slice(10);
  if (u.startsWith('webcal://'))  return 'https://' + u.slice(9);
  if (u.startsWith('icals://'))   return 'https://' + u.slice(8);
  if (u.startsWith('ical://'))    return 'https://' + u.slice(7);
  if (u.startsWith('http://'))    return 'https://' + u.slice(7);
  return u;
}

// ─── Kort per familiemedlem ───────────────────────────────────
function MemberItsCard({ member, onSynced }) {
  const [url,        setUrl]      = useState(member.url || '');
  const [interval,   setInterval_]= useState(member.interval || 60);
  const [savedUrl,   setSavedUrl] = useState(member.url || '');
  const [editing,    setEditing]  = useState(!member.url);
  const [syncing,    setSyncing]  = useState(false);
  const [msg,        setMsg]      = useState(null);

  const fmtTime = iso => iso
    ? new Date(iso).toLocaleString('nb-NO', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })
    : null;

  const saveUrl = async e => {
    e.preventDefault();
    setMsg(null);
    try {
      const normalized = normalizeIcalUrl(url);
      setUrl(normalized);
      await api.put('/api/itslearning/config', { url: normalized, interval, userId: member.userId });
      setSavedUrl(normalized);
      setEditing(false);
      setMsg({ type:'ok', text:'URL lagret! Synkroniserer…' });
      // Auto-synk umiddelbart etter lagring
      setSyncing(true);
      try {
        const r = await api.post('/api/itslearning/sync', { userId: member.userId });
        setMsg({ type:'ok', text:`✅ Lagret og synket: ${r.imported} nye, ${r.updated} oppdatert` });
        onSynced();
      } catch(syncErr) {
        setMsg({ type:'err', text: 'Lagret, men synk feilet: ' + (syncErr.response?.data?.error || syncErr.message) });
      } finally { setSyncing(false); }
    } catch(ex) {
      setMsg({ type:'err', text: ex.response?.data?.error || 'Lagring feilet' });
    }
  };

  const sync = async () => {
    setSyncing(true); setMsg(null);
    try {
      const r = await api.post('/api/itslearning/sync', { userId: member.userId });
      setMsg({ type:'ok', text:`✅ ${r.imported} nye, ${r.updated} oppdatert` });
      onSynced();
    } catch(ex) {
      setMsg({ type:'err', text: ex.response?.data?.error || 'Synk feilet' });
    } finally { setSyncing(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Fjerne Itslearning-kobling for ${member.name}?`)) return;
    try {
      await api.delete('/api/itslearning/remove', { data: { userId: member.userId } });
      setUrl(''); setEditing(true);
      setMsg({ type:'ok', text:'Kobling fjernet' });
      onSynced();
    } catch(ex) {
      setMsg({ type:'err', text: 'Fjerning feilet' });
    }
  };

  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:16, marginBottom:10 }}>
      {/* Topplinje med avatar og navn */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
        <div style={{ width:36, height:36, borderRadius:'50%', background: member.color || '#7C6FF7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'white', flexShrink:0 }}>
          {member.avatar || member.name?.[0] || '?'}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:700, color:'var(--txt)' }}>{member.name}</div>
          <div style={{ fontSize:11, color: savedUrl ? 'var(--green)' : 'var(--txl)' }}>
            {savedUrl ? '✅ Koblet til Itslearning' : '○ Ikke koblet til'}
          </div>
        </div>
        {savedUrl && !editing && (
          <div style={{ display:'flex', gap:6 }}>
            <button style={h.syncBtn} onClick={sync} disabled={syncing}>
              {syncing ? '⏳' : '↻ Synk'}
            </button>
            <button style={h.editUrlBtn} onClick={() => setEditing(true)}>✏️</button>
            <button style={h.removeBtn} onClick={remove}>🗑</button>
          </div>
        )}
      </div>

      {/* Sist synket */}
      {savedUrl && !editing && member.synced_at && (
        <div style={{ fontSize:11, color:'var(--txl)', marginBottom:8 }}>
          Sist synket: {fmtTime(member.synced_at)}
        </div>
      )}

      {/* Melding */}
      {msg && (
        <div style={{ padding:'8px 12px', borderRadius:9, marginBottom:10, fontSize:12,
          background: msg.type==='ok' ? 'var(--green-bg)' : 'var(--red-bg)',
          color:      msg.type==='ok' ? 'var(--green)'    : 'var(--red)' }}>
          {msg.text}
        </div>
      )}

      {/* Skjema */}
      {(editing || !savedUrl) && (
        <form onSubmit={saveUrl}>
          <input style={h.urlInp} value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="webcal://... eller https://..."
          />
          <div style={{ fontSize:11, color:'var(--txl)', marginBottom:8, lineHeight:1.5 }}>
            Støtter: webcal://, ical://, https://
          </div>
          <div style={h.intervalRow}>
            <label style={h.intervalLbl}>🔄 Automatisk synk</label>
            <select style={h.intervalSel} value={interval} onChange={e => setInterval_(Number(e.target.value))}>
              {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button style={h.saveBtn} type="submit">Lagre</button>
            {member.url && <button style={h.cancelBtn} type="button" onClick={() => setEditing(false)}>Avbryt</button>}
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Itslearning-seksjon ─────────────────────────────────────
function ItslearningSection({ onSynced }) {
  const [members,   setMembers]  = useState([]);
  const [showHelp,  setShowHelp] = useState(false);
  const [loading,   setLoading]  = useState(true);

  const load = () => {
    api.get('/api/itslearning/family')
      .then(d => setMembers(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSynced = () => { load(); onSynced(); };

  return (
    <div style={h.itslWrap}>
      <div style={h.itslHeader}>
        <div style={h.itslLogo}>📘</div>
        <div>
          <div style={h.itslTitle}>Itslearning</div>
          <div style={h.itslSub}>Koble til én eller flere familiemedlemmer</div>
        </div>
        <button style={h.helpBtn} onClick={() => setShowHelp(v => !v)}>
          {showHelp ? '✕ Lukk' : '? Hjelp'}
        </button>
      </div>

      {showHelp && (
        <div style={h.helpBox}>
          <strong>Slik finner du iCal-URL-en:</strong>
          <ol style={{ margin:'8px 0 0', paddingLeft:18, lineHeight:1.8 }}>
            <li>Logg inn på skolen sin Itslearning</li>
            <li>Gå til <strong>Kalender</strong></li>
            <li>Klikk <strong>«Abonner på kalender»</strong> eller <strong>«iCal»</strong></li>
            <li>Kopier URL-en og lim den inn</li>
          </ol>
          <p style={{ margin:'8px 0 0', color:'var(--txl)', fontSize:12 }}>
            Støttede formater: webcal://, ical://, https://
          </p>
        </div>
      )}

      {loading ? (
        <div style={{ padding:20, textAlign:'center', color:'var(--txl)' }}>Laster…</div>
      ) : (
        members.map(m => (
          <MemberItsCard key={m.userId} member={m} onSynced={handleSynced} />
        ))
      )}
    </div>
  );
}

const SUBJECTS = ['Matte','Norsk','Engelsk','Naturfag','Samfunnsfag','Gym','Kunst','Musikk','Annet'];
const COLORS   = ['#7C6FF7','#F7605C','#34D399','#FBBF24','#60A5FA','#F472B6'];

function Progress({ value }) {
  const pct = Math.min(100, Math.max(0, value||0));
  const color = pct === 100 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#7C6FF7';
  return (
    <div style={{ background:'var(--bdr)', borderRadius:20, height:6, overflow:'hidden', flex:1 }}>
      <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:20, transition:'.3s' }} />
    </div>
  );
}

export default function HomeworkPage() {
  const isMobile = useMobile();
  const { dark } = useTheme();
  const { members } = useAuth();
  const { data: homework, refresh } = useApi('/api/homework');
  const [tab, setTab] = useState('lekser'); // lekser | itslearning
  const [filterMember, setFilterMember] = useState('all');
  const [filterDone, setFilterDone]     = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm] = useState({ user_id:'', subject:'Matte', title:'', due_date:'' });

  const set = k => e => setForm(f => ({...f, [k]: e.target.value}));

  const save = async e => {
    e.preventDefault();
    await api.post('/api/homework', form);
    setShowForm(false);
    setForm({ user_id:'', subject:'Matte', title:'', due_date:'' });
    refresh();
  };

  const toggle = async (hw) => {
    await api.put(`/api/homework/${hw.id}`, { done: hw.done ? 0 : 1, progress: hw.done ? hw.progress : 100 });
    refresh();
  };

  const setProgress = async (hw, progress) => {
    await api.put(`/api/homework/${hw.id}`, { progress, done: progress === 100 ? 1 : 0 });
    refresh();
  };

  const filtered = (homework||[])
    .filter(h => filterMember === 'all' || h.user_id === filterMember)
    .filter(h => filterDone ? true : !h.done);

  // Grupper per fag
  const bySubject = filtered.reduce((acc, h) => {
    (acc[h.subject] = acc[h.subject]||[]).push(h);
    return acc;
  }, {});

  const isOverdue = hw => hw.due_date && !hw.done && new Date(hw.due_date) < new Date();

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';
  const border = dark ? 'rgba(255,255,255,.07)' : 'rgba(124,111,247,0.12)';
  const glassInp = { padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none' };

  if (isMobile) return (
    <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

      {/* Gradient Hero Header */}
      <div style={{
        background: dark
          ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
          : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
        padding: '52px 20px 20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
              <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Lekser</div>
            </div>
            {tab==='lekser' && <button onClick={()=>setShowForm(v=>!v)} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 14px', fontWeight:700, fontSize:14, cursor:'pointer' }}>{showForm?'Avbryt':'+ Ny'}</button>}
          </div>
          {/* Tab chips */}
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={()=>setTab('lekser')} style={{ padding:'7px 14px', borderRadius:20, border:'none', background:tab==='lekser'?'rgba(255,255,255,0.9)':'rgba(255,255,255,0.15)', color:tab==='lekser'?'#7C6FF7':'rgba(255,255,255,0.85)', fontWeight:700, fontSize:13, cursor:'pointer' }}>📚 Lekser</button>
            <button onClick={()=>setTab('itslearning')} style={{ padding:'7px 14px', borderRadius:20, border:'none', background:tab==='itslearning'?'rgba(255,255,255,0.9)':'rgba(255,255,255,0.15)', color:tab==='itslearning'?'#7C6FF7':'rgba(255,255,255,0.85)', fontWeight:700, fontSize:13, cursor:'pointer' }}>📘 Itslearning</button>
            {tab==='lekser' && <button onClick={()=>setFilterDone(v=>!v)} style={{ marginLeft:'auto', padding:'7px 14px', borderRadius:20, border:'none', background:'rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.85)', fontWeight:600, fontSize:12, cursor:'pointer' }}>{filterDone?'Skjul ferdige':'Vis ferdige'}</button>}
          </div>
        </div>
      </div>

      {tab==='itslearning' && (
        <div style={{ padding:14 }}><ItslearningSection onSynced={refresh}/></div>
      )}

      {tab==='lekser' && (
        <>
          {showForm && (
            <form onSubmit={save} className="glass-card" style={{ margin:'12px 16px', padding:14, display:'flex', flexDirection:'column', gap:10 }}>
              <input style={{...glassInp, width:'100%', boxSizing:'border-box'}} placeholder="Oppgave" value={form.title} onChange={set('title')} autoFocus required/>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <select style={{...glassInp, padding:'11px 12px'}} value={form.user_id} onChange={set('user_id')} required>
                  <option value="">Velg elev</option>
                  {members.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <select style={{...glassInp, padding:'11px 12px'}} value={form.subject} onChange={set('subject')}>
                  {SUBJECTS.map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <input type="date" style={{...glassInp, padding:'11px 14px'}} value={form.due_date} onChange={set('due_date')}/>
              <button type="submit" style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Legg til lekse</button>
            </form>
          )}
          <div style={{ padding:'12px 16px 80px' }}>
            {Object.keys(bySubject).length===0 && (
              <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📚</div>
                <div style={{ fontSize:16, fontWeight:700, color:txt }}>Ingen lekser</div>
              </div>
            )}
            {Object.entries(bySubject).map(([subj, items])=>(
              <div key={subj} style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#7C6FF7', textTransform:'uppercase', letterSpacing:.5, marginBottom:8, paddingLeft:4 }}>{subj}</div>
                {items.map(hw=>(
                  <div key={hw.id} className="glass-card" style={{ marginBottom:8, padding:'14px 16px', opacity:hw.done?0.6:1 }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                      <button onClick={()=>toggle(hw)} style={{ width:26, height:26, borderRadius:8, border:`2.5px solid ${hw.done?'#34D399':'#7C6FF7'}`, background:hw.done?'#34D399':'transparent', cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, fontWeight:800, marginTop:1 }}>
                        {hw.done&&'✓'}
                      </button>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:15, fontWeight:600, color:txt, textDecoration:hw.done?'line-through':'none', marginBottom:4 }}>{hw.title}</div>
                        <div style={{ fontSize:12, color:sub, display:'flex', gap:10 }}>
                          {hw.user_name && <span>👤 {hw.user_name}</span>}
                          {hw.due_date && <span style={{ color:isOverdue(hw)?'#F7605C':sub }}>📅 {hw.due_date}</span>}
                        </div>
                        {!hw.done && (
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
                            <div style={{ flex:1, height:5, background:border, borderRadius:99, overflow:'hidden' }}>
                              <div style={{ width:`${hw.progress||0}%`, height:'100%', background:'linear-gradient(135deg,#7C6FF7,#9358E8)', borderRadius:99 }}/>
                            </div>
                            <span style={{ fontSize:11, color:sub, fontWeight:700 }}>{hw.progress||0}%</span>
                            {[0,25,50,75,100].map(p=>(
                              <button key={p} onClick={()=>setProgress(hw,p)} style={{ background:hw.progress===p?'linear-gradient(135deg,#7C6FF7,#9358E8)':dark?'rgba(255,255,255,0.08)':'rgba(124,111,247,0.10)', color:hw.progress===p?'#fff':sub, border:'none', borderRadius:6, padding:'3px 7px', fontSize:10, cursor:'pointer', fontWeight:700 }}>{p}%</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div style={s.wrap}>
      {/* Tabs */}
      <div style={s.tabs}>
        <button style={{...s.tab,...(tab==='lekser'?s.tabActive:{})}} onClick={()=>setTab('lekser')}>📚 Lekser</button>
        <button style={{...s.tab,...(tab==='itslearning'?s.tabActive:{})}} onClick={()=>setTab('itslearning')}>📘 Itslearning</button>
      </div>

      {tab === 'itslearning' && <ItslearningSection onSynced={refresh} />}

      {tab === 'lekser' && <>
      {/* Toolbar */}
      <div style={s.toolbar}>
        <div style={s.filters}>
          <select style={s.sel} value={filterMember} onChange={e => setFilterMember(e.target.value)}>
            <option value="all">Alle barn</option>
            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <label style={s.checkLabel}>
            <input type="checkbox" checked={filterDone} onChange={e => setFilterDone(e.target.checked)} />
            Vis ferdige
          </label>
        </div>
        <button style={s.newBtn} onClick={() => setShowForm(v=>!v)}>+ Ny lekse</button>
      </div>

      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>Ny lekse</div>
          <form onSubmit={save} style={s.form}>
            <div style={s.row}>
              <div style={s.field}>
                <label style={s.lbl}>Barn</label>
                <select style={s.sel} value={form.user_id} onChange={set('user_id')} required>
                  <option value="">Velg barn</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.lbl}>Fag</label>
                <select style={s.sel} value={form.subject} onChange={set('subject')}>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <input style={s.inp} placeholder="Hva skal gjøres?" value={form.title} onChange={set('title')} required />
            <div style={s.field}>
              <label style={s.lbl}>Frist</label>
              <input style={s.inp} type="date" value={form.due_date} onChange={set('due_date')} />
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={s.saveBtn} type="submit">Lagre</button>
              <button style={s.cancelBtn} type="button" onClick={() => setShowForm(false)}>Avbryt</button>
            </div>
          </form>
        </div>
      )}

      {filtered.length === 0 && <div style={s.empty}>Ingen lekser å vise 🎉</div>}

      {Object.entries(bySubject).map(([subject, items]) => (
        <div key={subject}>
          <div style={s.subjectLabel}>{subject}</div>
          {items.map(hw => (
            <div key={hw.id} style={{...s.card, ...(hw.done?s.cardDone:{}), ...(isOverdue(hw)?s.cardOverdue:{})}}>
              <div style={s.cardTop}>
                <button style={{...s.checkbox, ...(hw.done?s.checkboxDone:{})}} onClick={() => toggle(hw)}>
                  {hw.done ? '✓' : ''}
                </button>
                <div style={s.cardBody}>
                  <div style={{...s.hwTitle, ...(hw.done?s.hwTitleDone:{})}}>{hw.title}</div>
                  <div style={s.hwMeta}>
                    <div style={{...s.memberTag, background: (hw.color||'#7C6FF7')+'22', color: hw.color||'#7C6FF7'}}>
                      {hw.avatar} {hw.user_name}
                    </div>
                    {hw.source === 'itslearning' && (
                      <span style={s.itslBadge}>📘 Itslearning</span>
                    )}
                    {hw.due_date && (
                      <span style={{color: isOverdue(hw)?'#F7605C':'var(--txl)'}}>
                        {isOverdue(hw)?'⚠️ Forfalt':'📅'} {new Date(hw.due_date).toLocaleDateString('nb-NO',{day:'numeric',month:'short'})}
                      </span>
                    )}
                  </div>
                  {!hw.done && (
                    <div style={s.progressRow}>
                      <Progress value={hw.progress} />
                      <span style={s.pct}>{hw.progress||0}%</span>
                      <div style={s.pctBtns}>
                        {[25,50,75,100].map(p => (
                          <button key={p} style={{...s.pctBtn, ...(hw.progress===p?s.pctBtnActive:{})}}
                            onClick={() => setProgress(hw, p)}>{p}%</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
      </>}
    </div>
  );
}

const s = {
  wrap:         { display:'flex', flexDirection:'column', gap:10 },
  tabs:         { display:'flex', gap:4, background:'var(--card)', padding:4, borderRadius:12, border:'1px solid var(--bdr)' },
  tab:          { flex:1, padding:'8px', borderRadius:9, border:'none', background:'transparent', color:'var(--txm)', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  tabActive:    { background:'#7C6FF7', color:'#fff' },
  toolbar:      { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 },
  filters:      { display:'flex', alignItems:'center', gap:12 },
  sel:          { padding:'8px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)', cursor:'pointer' },
  checkLabel:   { display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--txm)', cursor:'pointer' },
  newBtn:       { padding:'9px 16px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  formCard:     { background:'var(--card)', borderRadius:16, padding:18, border:'1px solid var(--bdr)' },
  formTitle:    { fontSize:15, fontWeight:800, color:'var(--txt)', marginBottom:14 },
  form:         { display:'flex', flexDirection:'column', gap:10 },
  row:          { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  field:        { display:'flex', flexDirection:'column', gap:4 },
  lbl:          { fontSize:11, fontWeight:700, color:'var(--txm)', textTransform:'uppercase', letterSpacing:.4 },
  inp:          { padding:'9px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  saveBtn:      { flex:1, padding:'9px', borderRadius:9, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  cancelBtn:    { flex:1, padding:'9px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  empty:        { color:'var(--txl)', fontSize:14, padding:'30px 0', textAlign:'center' },
  subjectLabel: { fontSize:12, fontWeight:800, color:'#7C6FF7', textTransform:'uppercase', letterSpacing:.5, margin:'12px 0 6px', paddingLeft:2 },
  card:         { background:'var(--card)', borderRadius:12, padding:'12px 14px', border:'1px solid var(--bdr)', marginBottom:4 },
  cardDone:     { opacity:.6 },
  cardOverdue:  { borderColor:'#FCA5A5', background:'#FFF5F5' },
  cardTop:      { display:'flex', gap:12, alignItems:'flex-start' },
  checkbox:     { width:22, height:22, borderRadius:6, border:'2px solid var(--bdr)', background:'var(--card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0, marginTop:1 },
  checkboxDone: { background:'#34D399', borderColor:'#34D399' },
  cardBody:     { flex:1, minWidth:0 },
  hwTitle:      { fontSize:14, fontWeight:700, color:'var(--txt)', marginBottom:4 },
  hwTitleDone:  { textDecoration:'line-through', color:'var(--txl)' },
  hwMeta:       { display:'flex', flexWrap:'wrap', gap:8, alignItems:'center', marginBottom:6, fontSize:12, color:'var(--txl)' },
  memberTag:    { padding:'2px 8px', borderRadius:20, fontWeight:700, fontSize:12 },
  progressRow:  { display:'flex', alignItems:'center', gap:8 },
  pct:          { fontSize:11, fontWeight:700, color:'var(--txm)', flexShrink:0 },
  pctBtns:      { display:'flex', gap:3 },
  pctBtn:       { padding:'2px 5px', borderRadius:5, border:'1px solid var(--bdr)', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:10, fontFamily:'inherit' },
  pctBtnActive: { background:'#7C6FF7', color:'#fff', borderColor:'#7C6FF7' },
  itslBadge:   { fontSize:10, fontWeight:700, background:'#EFF6FF', color:'#3B82F6', padding:'2px 7px', borderRadius:20 },
};

// Itslearning-seksjon styles
const h = {
  itslWrap:      { background:'var(--card)', borderRadius:16, padding:'16px 18px', border:'1px solid var(--bdr)', display:'flex', flexDirection:'column', gap:12 },
  itslHeader:    { display:'flex', alignItems:'center', gap:12 },
  itslLogo:      { width:40, height:40, borderRadius:12, background:'#EFF6FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 },
  itslTitle:     { fontSize:15, fontWeight:800, color:'var(--txt)' },
  itslSub:       { fontSize:12, color:'var(--txl)', marginTop:1 },
  helpBtn:       { marginLeft:'auto', padding:'5px 12px', borderRadius:8, border:'1px solid var(--bdr)', background:'var(--bg)', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', flexShrink:0 },
  helpBox:       { background:'var(--bg)', borderRadius:10, padding:'12px 14px', fontSize:13, color:'var(--txm)', lineHeight:1.6 },
  msg:           { padding:'10px 14px', borderRadius:10, fontSize:13, fontWeight:600 },
  urlForm:       { display:'flex', flexDirection:'column', gap:8 },
  urlInp:        { padding:'10px 12px', borderRadius:10, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)' },
  saveBtn:       { padding:'9px 18px', borderRadius:10, border:'none', background:'#7C6FF7', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  cancelBtn:     { padding:'9px 14px', borderRadius:10, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  connectedWrap: { display:'flex', flexDirection:'column', gap:8 },
  connectedBar:  { display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 },
  connectedInfo: { display:'flex', alignItems:'center', gap:10 },
  connectedDot:  { fontSize:18 },
  connectedLabel:{ fontSize:13, fontWeight:700, color:'var(--txt)' },
  connectedTime: { fontSize:11, color:'var(--txl)', marginTop:1 },
  syncBtn:       { padding:'7px 14px', borderRadius:9, border:'none', background:'#3B82F6', color:'#fff', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' },
  editUrlBtn:    { width:32, height:32, borderRadius:9, border:'1px solid var(--bdr)', background:'var(--bg)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' },
  removeBtn:     { width:32, height:32, borderRadius:9, border:'1px solid #FCA5A5', background:'#FFF5F5', color:'var(--red)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' },
  autoSyncBar:   { display:'flex', alignItems:'center', gap:8, background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:10, padding:'8px 12px', fontSize:12, color:'#166534', flexWrap:'wrap' },
  autoSyncIcon:  { fontSize:15, flexShrink:0 },
  changeBtn:     { marginLeft:'auto', padding:'3px 10px', borderRadius:8, border:'1px solid #86EFAC', background:'var(--card)', color:'#166534', fontWeight:700, cursor:'pointer', fontSize:11, fontFamily:'inherit' },
  intervalRow:   { display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' },
  intervalLbl:   { fontSize:13, fontWeight:600, color:'var(--txm)', flexShrink:0 },
  intervalSel:   { flex:1, padding:'8px 12px', borderRadius:9, border:'1px solid var(--input-bdr)', fontFamily:'inherit', fontSize:13, color:'var(--input-txt)', outline:'none', background:'var(--input-bg)', cursor:'pointer', minWidth:160 },
};
